import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import axios from 'axios'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import Antd from 'ant-design-vue'
import 'ant-design-vue/dist/reset.css'
import customLocale from './utils/locale'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-tw'

import App from './App.vue'
import router from './router'
import { useFontSettingsStore } from './stores/fontSettings'
// 導入版本檢查服務
import { checkVersionFromResponse } from './services/versionCheck'

import './assets/main.css'

// 自定義繁體中文 locale，確保月份顯示為繁體中文
dayjs.locale('zh-tw-custom', {
  name: 'zh-tw-custom',
  weekdays: '星期日_星期一_星期二_星期三_星期四_星期五_星期六'.split('_'),
  weekdaysShort: '週日_週一_週二_週三_週四_週五_週六'.split('_'),
  weekdaysMin: '日_一_二_三_四_五_六'.split('_'),
  months: '一月_二月_三月_四月_五月_六月_七月_八月_九月_十月_十一月_十二月'.split('_'),
  monthsShort: '一月_二月_三月_四月_五月_六月_七月_八月_九月_十月_十一月_十二月'.split('_'),
  ordinal: n => `${n}日`,
  weekStart: 1,
  yearStart: 4,
  formats: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'YYYY/MM/DD',
    LL: 'YYYY年M月D日',
    LLL: 'YYYY年M月D日 HH:mm',
    LLLL: 'YYYY年M月D日dddd HH:mm',
    l: 'YYYY/M/D',
    ll: 'YYYY年M月D日',
    lll: 'YYYY年M月D日 HH:mm',
    llll: 'YYYY年M月D日dddd HH:mm'
  },
  relativeTime: {
    future: '%s後',
    past: '%s前',
    s: '幾秒',
    m: '1分鐘',
    mm: '%d分鐘',
    h: '1小時',
    hh: '%d小時',
    d: '1天',
    dd: '%d天',
    M: '1個月',
    MM: '%d個月',
    y: '1年',
    yy: '%d年'
  }
});

// 使用自定義的繁體中文 locale
dayjs.locale('zh-tw-custom');

// 配置全局 axios
import { getBackendUrl, safeGetSessionStorage, safeGetLocalStorage } from './utils/backendUtils'
import { ensureProductImageToken } from './utils/productImageToken'

axios.defaults.baseURL = getBackendUrl()

// 添加請求攔截器，自動添加認證令牌
axios.interceptors.request.use(
  async config => {
    // 從sessionStorage獲取token（支援多帳號同時登入）
    const token = safeGetSessionStorage('admin_token');
    if (token) {
      // 為請求添加Authorization頭
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 添加響應攔截器，處理版本檢查和token過期
axios.interceptors.response.use(
  async response => {
    // 檢查版本是否一致（從響應頭獲取版本號）
    const token = safeGetSessionStorage('admin_token');
    if (token) {
      // 排除版本檢查 API 本身，避免循環檢查
      const isVersionCheckApi = response.config?.url && (
        response.config.url.includes('/api/version-check') || 
        response.config.url.includes('/admin/version-check') ||
        response.config.url.includes('/version-check')
      );
      
      if (!isVersionCheckApi) {
        // 從響應頭檢查版本（使用防抖機制，避免過於頻繁）
        const isVersionMatch = checkVersionFromResponse(response);
        if (!isVersionMatch) {
          // 版本不一致，執行登出
          executeVersionMismatchLogout();
          return Promise.reject(new Error('版本不匹配'));
        }
      }
    }
    return response;
  },
  async error => {
    if (error.response && error.response.status === 401) {
      // Token過期或無效，改用「記住我」的續期票換發，不需要密碼
      try {
        const savedSession = localStorage.getItem('admin_refresh_session')
        const isRefreshRequest = error.config?.url?.includes('/admin/refresh-token')

        if (savedSession && !isRefreshRequest) {
          const { refreshToken, database } = JSON.parse(savedSession)
          if (refreshToken && database) {
            const refreshResponse = await axios.post('/admin/refresh-token', {
              refreshToken,
              database
            })

            if (refreshResponse.data.success) {
              // 續期成功，更新token（使用sessionStorage支援多帳號）
              const { token: newToken, username: newUsername, role, database: newDatabase, databaseName } = refreshResponse.data
              sessionStorage.setItem('admin_token', newToken)
              sessionStorage.setItem('admin_user', JSON.stringify({ username: newUsername, role, database: newDatabase, databaseName }))
              axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

              // 重新發送原始請求
              const originalRequest = error.config
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`
              return axios(originalRequest)
            }
          }
        }
      } catch (refreshError) {
        console.warn('續期失敗:', refreshError)
        // 票已被作廢或過期就不再保留
        if (refreshError.response?.status === 401) {
          localStorage.removeItem('admin_refresh_session')
        }
      }

      // 如果自動重新登入失敗，清除會話存儲並跳轉到登入頁面
      try {
        sessionStorage.removeItem('admin_token')
        sessionStorage.removeItem('admin_user')
      } catch (e) {
        console.warn('清除會話存儲失敗:', e)
      }
      // 跳轉到登入頁面
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
);

// 設置全局拖曳事件處理，防止拖曳時的默認行為干擾
document.addEventListener('dragover', function(e) {
  e.preventDefault();
}, false);

document.addEventListener('drop', function(e) {
  e.preventDefault();
}, false);

// 增加全局變數來追蹤拖曳狀態
window.dragState = {
  isDragging: false,
  draggedNode: null,
  targetNode: null
};

// 長時間停留在同一頁時，背景更新商品圖片通行證
setInterval(() => {
  ensureProductImageToken()
}, 30 * 60 * 1000)

const app = createApp(App)

// 創建 Pinia 實例
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

// 全局配置 Ant Design 本地化
app.provide('antLocale', customLocale);

app.use(pinia)
app.use(router)
app.use(ElementPlus)
app.use(Antd)

// 設定全局 router 實例供 WebSocket 服務使用
window.vueRouter = router

// 執行版本不一致登出的函數
function executeVersionMismatchLogout() {
  // 清除會話存儲
  try {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
  } catch (e) {
    console.warn('清除存儲失敗:', e);
  }
  
  // 清除 axios 認證頭
  delete axios.defaults.headers.common['Authorization'];
  
  // 跳轉到登入頁面
  if (router.currentRoute.value.path !== '/login') {
    router.push({
      path: '/login',
      query: { message: 'version_mismatch' }
    });
  }
}

app.mount('#app')

// 在應用掛載後初始化字體設定
setTimeout(() => {
  const fontSettingsStore = useFontSettingsStore()
  fontSettingsStore.initializeFontSettings()
}, 0)